/**
 * Pure projections from the event log to habit read models (`HabitView`,
 * `DueHabitView` -- domain-api.ts). Mirrors `objectives/projection.ts`'s
 * shape: no I/O; `today`/`date` are explicit parameters (ADR-0001 rule 2,
 * ADR-0006 "no Date.now() inside src/domain").
 */

import type {
  CompassEvent,
  DueHabitView,
  HabitId,
  HabitView,
} from '../../contracts';
import {
  avoidStatusFor,
  buildStatusFor,
  computeAvoidLogStatuses,
  computeBuildLogStatuses,
  countBuildCompletionsInWeek,
  countLapsesInWeek,
} from './logging';
import { buildHabitStates, weeklyPlannedFor, type HabitState } from './state';
import { weekOf } from './week';

/** Full `HabitView` list for `listHabits()` (HB-16/AC-B17), in creation order. */
export function projectHabitViews(
  events: readonly CompassEvent[],
  options?: { readonly includeArchived?: boolean },
): readonly HabitView[] {
  const states = buildHabitStates(events);
  const includeArchived = options?.includeArchived ?? false;

  const views: HabitView[] = [];
  for (const state of states.values()) {
    if (state.lifecycle === 'archived' && !includeArchived) continue;
    views.push(toHabitView(state));
  }
  // Creation order falls out of UUIDv7 entity IDs (ADR-0003), same
  // convention as `projectObjectiveSummaries`.
  return views.sort((a, b) => (a.habitId < b.habitId ? -1 : a.habitId > b.habitId ? 1 : 0));
}

export function projectHabitView(
  events: readonly CompassEvent[],
  habitId: HabitId,
): HabitView | null {
  const states = buildHabitStates(events);
  const state = states.get(habitId);
  return state ? toHabitView(state) : null;
}

function toHabitView(state: HabitState): HabitView {
  return {
    habitId: state.habitId,
    name: state.name,
    category: state.category,
    plan: state.plan,
    objectiveId: state.objectiveId,
    state: state.lifecycle,
  };
}

/**
 * The `DailyPageView.habits` list for one day (HB-6/HB-7/HB-9): due-today
 * build habits (not yet at their weekly target, PRD §8 "the page lists
 * work, not trophies", AC-B8) and avoid habits with remaining allowance,
 * PLUS avoid habits over allowance (still rendered for reference in
 * "Keeping in Check", §2.2 of phase-2b-habits.md -- the domain layer returns
 * every non-paused/non-archived habit's row and lets the UI section it by
 * `status`/`allowance`, since `DueHabitView` already carries everything
 * needed to distinguish "due", "satisfied for the week", and "over
 * allowance" without the domain layer making a UI-sectioning decision).
 *
 * Ordering (AC-B9): build habits first (daily-page-visible order = creation
 * order, same tie-break as `projectHabitViews`), then avoid habits. The
 * Today-view composition layer (dev-ui) interleaves these with actions per
 * §2.2's exact rule; this function only guarantees "build habits, then
 * avoid habits" within its own returned list, matching the domain/UI
 * ownership split objectives/due-actions.ts already established (domain
 * picks candidates and a stable order; the UI's density cap and
 * cross-kind interleave live at the composition layer, not here).
 *
 * Paused/archived habits are excluded entirely (AC-B4: "no longer appears
 * in getDailyPage's due list (any day)").
 */
export function computeDueHabits(
  events: readonly CompassEvent[],
  date: string,
): readonly DueHabitView[] {
  const states = buildHabitStates(events);
  const buildStatuses = computeBuildLogStatuses(events);
  const avoidStatuses = computeAvoidLogStatuses(events);
  const weekStart = weekOf(date);

  const buildRows: DueHabitView[] = [];
  const avoidRows: DueHabitView[] = [];

  for (const state of states.values()) {
    if (state.lifecycle !== 'active') continue;

    if (state.plan.habitKind === 'build') {
      const target = weeklyTargetFor(state);
      const done = countBuildCompletionsInWeek(events, state.habitId, weekStart);
      const status = buildStatusFor(buildStatuses, state.habitId, date);

      // AC-B8: an N-times/week (or daily, target 7) build habit that already
      // met its weekly target drops off the due list for the rest of the
      // week -- UNLESS today's own tap is what satisfied it (status is
      // 'done'/'skipped' for today), in which case it must still render so
      // the row can show its completed state and support undo (AC-B10).
      const alreadyMetBeforeConsideringToday =
        done >= target && status.kind === 'pending';
      if (alreadyMetBeforeConsideringToday) continue;

      buildRows.push({
        habitKind: 'build',
        habitId: state.habitId,
        name: state.name,
        category: state.category,
        status: status.kind,
        weekProgress: { done, target },
        // Study-session capture UI is out of scope this phase (HB-10) --
        // the field is typed on the contract for a future phase, but this
        // build never offers it, matching "a study habit completes with a
        // bare tap like any other build habit" (phase-2b-habits.md HB-10).
        offersStudyLog: false,
        entryEventId: status.kind === 'pending' ? null : status.entryEventId,
        minutesLoggedToday: 0,
      });
    } else {
      const limit = state.plan.schedule.limitPerWeek;
      const used = countLapsesInWeek(events, state.habitId, weekStart);
      const status = avoidStatusFor(avoidStatuses, state.habitId, date);

      avoidRows.push({
        habitKind: 'avoid',
        habitId: state.habitId,
        name: state.name,
        category: state.category,
        status: status.kind,
        lapsesToday: status.kind === 'lapsed' ? status.lapsesToday : 0,
        allowance: { used, limit },
        entryEventId: status.kind === 'pending' ? null : status.entryEventId,
      });
    }
  }

  buildRows.sort((a, b) => (a.habitId < b.habitId ? -1 : a.habitId > b.habitId ? 1 : 0));
  avoidRows.sort((a, b) => (a.habitId < b.habitId ? -1 : a.habitId > b.habitId ? 1 : 0));

  return [...buildRows, ...avoidRows];
}

/** Daily habits count as target 7 (one per day) for weekProgress rendering ("2/3" vs a daily habit's own denominator). */
function weeklyTargetFor(state: HabitState): number {
  if (state.plan.habitKind !== 'build') return 0;
  return weeklyPlannedFor(state.plan);
}
